import { MapElement } from './map-element.model';
import { Point, GeometryObject, Geometry } from 'geojson';
import { Observable } from 'rxjs';
import { Layer } from './layer/layer.model';
import { ILayer } from './layer/layer.type';
import { MapPlace } from './types';
import { IMeasure } from './mapbox/utils/measure';
import { PointLike } from 'mapbox-gl';
import { LayerGroup } from './layer/layer-group.model';

export interface MapMarker {
  remove(): void;
  setLngLat(coords: MapLatLng): void;
}

export interface MapBounds {
  contains(coords): boolean;
  getNorthEast(): MapLatLng;
  getSouthWest(): MapLatLng;
}

export interface MapStyle {
  name: string;
  url: string;
}

export interface MapLatLng {
  lat: number;
  lng: number;
}

export enum MarkerMode {
  HTML = 'large',
  Canvas = 'small',
}

export abstract class MapService {
  public abstract layers$: Observable<Layer[]>;
  public abstract layersR$: Observable<Layer>;
  public abstract layerGroups$: Observable<LayerGroup[]>;
  public abstract loadedLayers$: Observable<Layer>;
  public abstract notLoadedLayers$: Observable<Set<Layer>>;
  public abstract visibleMarkers$: Observable<MapElement<Point>[]>;
  public abstract currentFeature$: Observable<MapElement<GeometryObject> | null>;
  public abstract hoverFeature$: Observable<{ data: MapElement<Geometry>, event: any }>;
  public abstract center$: Observable<MapLatLng>;
  public abstract zoom$: Observable<number>;
  public abstract measure: IMeasure;
  public abstract markerMode$: Observable<MarkerMode>;
  public abstract getLayerFeatures(): Map<string, MapElement<Geometry>[]>;

  public abstract initMap(element: HTMLElement, streetView?: boolean): Promise<void>;
  public abstract addLayer(layer: ILayer): Promise<Layer>;
  public abstract getLayersFromBackend(): Promise<ILayer[]>;
  public abstract createNewMarker(latLng: MapLatLng, markerElement: HTMLElement, offset?: PointLike): MapMarker;
  public abstract getLayer(id: string): Layer;
  public abstract getLayerList(): Layer[];
  public abstract changeCurrentFeature(feature: MapElement<GeometryObject> | null): void;
  public abstract changeHoverFeature(data: { data: MapElement<Point>, event: any }): void;
  public abstract getFeatureById(featureId: string): MapElement<Geometry>;
  public abstract getAvailableStyles(): MapStyle[];
  public abstract setStyle(style: MapStyle): void;
  public abstract getStyle(): MapStyle;
  public abstract setCenter(latLng: MapLatLng): void;
  public abstract getCenter(): MapLatLng;
  public abstract getBounds(): MapBounds;
  public abstract restrictBounds(bounds: {sw: MapLatLng, ne: MapLatLng}): void;
  public abstract zoomIn(): void;
  public abstract zoomOut(): void;
  public abstract zoomTo(level: number): void;
  public abstract getZoom(): number;
  public abstract flyTo(center: MapLatLng, zoom?: number): void;
  public abstract mapToScreenCoordinates(center: MapLatLng): { clientX: number, clientY: number };
  public abstract getBearing(): number;
  public abstract getMap(): any;
  public abstract getAccessToken(): string;
  public abstract getMarker(markerId: string): MapElement<Point>;
  public abstract searchResults(query: string, language?: string): Promise<MapPlace[]>;
  public abstract showPlaceOnMap(place: MapPlace): Promise<void>;
  public abstract showVivoAssetOnMap(regoId: string): void;
  public abstract setMarkerMode(mode: MarkerMode): void;
  public abstract getHasMultipleMarkerModes(): boolean;
}
